/**
 * Intelligems Custom Metrics — librairie
 *
 * Register un event : nom Intelligems + once (localStorage si true).
 * https://docs.intelligems.io/analytics/custom-events/custom-javascript-events
 */

const STORAGE_KEY = 'ig_metrics_once';
const MAX_NAME_LENGTH = 100;

/**
 * @typedef {Object} IntelligemsRegisterOptions
 * @property {string} name - Identifiant Intelligems (max 100 car.)
 * @property {boolean} [once=false] - Si true : 1 seul envoi, puis bloqué via localStorage
 * @property {string} [on] - Event DOM à écouter (défaut = name)
 */

class IntelligemsMetrics {
  #listeners = [];

  /**
   * Enregistre un event custom.
   *
   * @example
   * IntelligemsMetrics.register({ name: 'productAddToCart', once: false, on: 'product:add-to-cart' })
   * IntelligemsMetrics.register('productCarouselNavigate', { once: true, on: 'product-carousel:navigate' })
   *
   * @param {string | IntelligemsRegisterOptions} nameOrConfig
   * @param {Omit<IntelligemsRegisterOptions, 'name'>} [options]
   */
  register(nameOrConfig, options = {}) {
    const config =
      typeof nameOrConfig === 'string' ? { name: nameOrConfig, ...options } : { ...nameOrConfig };

    const name = config.name?.trim();
    const once = Boolean(config.once);
    const on = (config.on || name)?.trim();

    if (!name || !on) {
      console.log('[IntelligemsMetrics] register() ignored: name required');
      return this;
    }

    if (name.length > MAX_NAME_LENGTH) {
      console.log('[IntelligemsMetrics] register() ignored: name too long', name);
      return this;
    }

    const handler = () => this.track(name, { once });
    document.addEventListener(on, handler);
    this.#listeners.push({ on, handler, name, once });
    console.log('[IntelligemsMetrics] registered', { name, once, on });
    return this;
  }

  /**
   * Push immédiat vers Intelligems.
   * @param {string} name
   * @param {{ once?: boolean }} [options]
   * @returns {boolean}
   */
  track(name, { once = false } = {}) {
    const eventName = name?.trim();
    if (!eventName) return false;

    if (once && this.#hasFired(eventName)) {
      console.log('[IntelligemsMetrics] skipped (once)', eventName);
      return false;
    }

    window.igEvents = window.igEvents || [];
    window.igEvents.push({ event: eventName });

    if (once) this.#markFired(eventName);

    console.log('[IntelligemsMetrics] tracked', eventName, once ? '(once)' : '');
    return true;
  }

  /** Efface le flag once (debug / reset). */
  clearOnce(name) {
    try {
      if (!name) {
        localStorage.removeItem(STORAGE_KEY);
        console.log('[IntelligemsMetrics] clearOnce: all');
        return this;
      }

      const data = this.#readStore();
      delete data[name];
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
      console.log('[IntelligemsMetrics] clearOnce', name);
    } catch {
      // ignore
    }
    return this;
  }

  #hasFired(name) {
    return Boolean(this.#readStore()[name]);
  }

  #markFired(name) {
    try {
      const data = this.#readStore();
      data[name] = Date.now();
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch {
      // ignore (private mode, etc.)
    }
  }

  #readStore() {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}') || {};
    } catch {
      return {};
    }
  }
}

const metrics = new IntelligemsMetrics();

window.IntelligemsMetrics = metrics;

export default metrics;
